"use client";

import { IColumnDef } from "./types";
import { useState, useRef, useEffect, useCallback } from "react";
import { GripVerticalIcon, SlidersIcon } from "@/components/icons";
import { Button, Checkbox } from "@/components/atoms";
import { PopoverPortal } from "@/components/molecules/Popover";
import classNames from "classnames";
import { RichMetric, Widget } from "@/modules";

interface Props {
    widget: Widget;
    columns: IColumnDef[];
    drillDownTable: boolean;
    onReorder?: (newColumns: IColumnDef[]) => void;
    onChangeVisibility?: (newColumns: IColumnDef[]) => void;
}

export const Columns = ({ columns, drillDownTable, onChangeVisibility }: Props) => {
    const [orderedColumns, setOrderedColumns] = useState<IColumnDef[]>(columns);
    const latestColumnsRef = useRef<IColumnDef[]>(columns);
    const [isOpen, setIsOpen] = useState<boolean>(false);

    useEffect(() => {
        setOrderedColumns(columns);
    }, [columns]);


    const reorderableColumn = useCallback(
        (index: number, column: IColumnDef) => {
            if (column.type === "dimension" && !index) return false;
            if (!drillDownTable) return true;
            return index > 0;
        },
        [drillDownTable]
    );

    const toggleColumnVisibility = (column: IColumnDef) => {
        const nextColumn = { ...column, hidden: !column.hidden };
        const newColumns = orderedColumns.map(col =>
            col.header === column.header ? nextColumn : col
        );
        setOrderedColumns(newColumns);
        latestColumnsRef.current = newColumns;
        onChangeVisibility?.(newColumns);
    };

    return (
        <PopoverPortal
            align="right"
            trigger={
                <Button 
                    variant="filter" 
                    size="sm"
                    className={classNames({
                        '!border-[#2D3A3F]': isOpen
                    })}
                >
                    <SlidersIcon />
                    <span style={{ fontFamily: "Roboto, sans-serif" }}>Columns</span>
                </Button>
            }
            onOpenChange={setIsOpen}
        >
            <div className="p-2">
                <div className="space-y-1">
                    {orderedColumns.map((column, index) => (
                        <div
                            key={`${column.field || column.metric}-${index}`}
                            className={classNames("flex items-center gap-2 px-2 py-2 rounded hover:bg-gray-50", {
                                "opacity-50 cursor-not-allowed!": !reorderableColumn(index, column),
                            })}
                        >
                            <Checkbox
                                checked={!column.hidden}
                                className={classNames({
                                    "pointer-events-none": !reorderableColumn(index, column),
                                })}

                                onClick={() => toggleColumnVisibility(column)}
                            />
                            <span className="text-sm text-gray-700 truncate">{column.header}</span>
                        </div>
                    ))}
                </div>
            </div>
        </PopoverPortal>
    );
};
